/*
  aurala_sticky-atc.js
  The one script owned by sections/aurala_sticky-atc.liquid. It defines one custom element,
  <aurala-sticky-atc>, and adds nothing to window, document or any other element.

  Every lookup for something the bar owns goes through this.querySelector, so the element can
  only ever read inside its own root. There is exactly one lookup outside that root, and it is
  unavoidable: the bar reveals itself when the MAIN buybox leaves the viewport, so it has to
  hold a reference to it. It resolves that once, from the form id the section put in a data
  attribute, and never queries the document again.

  No document level click delegate: the button is a plain submit button aimed at the main form
  through form="", so the browser routes the submit and Dawn's product-form.js handles it.
  No setInterval and no scroll listener: IntersectionObserver reports the buybox entering and
  leaving the viewport, so the browser decides when to notify us.
*/

if (!customElements.get('aurala-sticky-atc')) {
  customElements.define(
    'aurala-sticky-atc',
    class AuralaStickyAtc extends HTMLElement {
      connectedCallback() {
        if (this.observer) return;

        this.submitButton = this.querySelector('[data-aurala-sticky-submit]');
        this.labelTarget = this.querySelector('[data-aurala-sticky-label]');
        this.priceTarget = this.querySelector('[data-aurala-sticky-price]');

        this.mainForm = this.resolveMainForm();
        if (!this.mainForm) return;

        /*
          Observe the whole buybox, not just its form. Two reasons, the second measured:
            - "when the main buybox scrolls out of view" is about the buybox, and the form is
              only the button at the bottom of it.
            - IntersectionObserver reports THRESHOLD CROSSINGS, not positions. A jump that
              takes an element straight from below the viewport to above it, one anchor link
              or one scrollTo, never crosses zero and fires nothing. product-info starts at
              the top of a product page, so it is intersecting on arrival, which means any
              jump away from the top does change the bucket and does fire.
        */
        this.watched = this.mainForm.closest('product-info') || this.mainForm;
        this.observer = new IntersectionObserver(this.onIntersection.bind(this), { threshold: 0 });
        this.observer.observe(this.watched);

        // Dawn's own pub/sub, so the bar's price and sold out state follow the variant the
        // customer picked in the main buybox rather than showing the first variant forever.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        this.observer = null;
        if (this.variantUnsubscriber) this.variantUnsubscriber();
        this.variantUnsubscriber = null;
      }

      /*
        The configured id first. The fallback exists because a wrong id would otherwise break
        the bar silently in both directions at once: nothing to observe, so it never appears,
        and a form="" pointing at nothing, so the button posts nothing. When the fallback is
        used the button's form attribute is repaired from the element actually found, so the
        two can never disagree.
      */
      resolveMainForm() {
        const byId = document.getElementById(this.dataset.mainFormId);
        if (byId) return byId;

        // data-type="add-to-cart-form" is what separates Dawn's real buy form from the
        // installment form, which sits earlier in the DOM and posts to /cart/add as well.
        const found = document.querySelector('product-info form[data-type="add-to-cart-form"]');
        if (!found || !found.id) return null;
        if (this.submitButton) this.submitButton.setAttribute('form', found.id);
        return found;
      }

      /*
        Visible only once the buybox has been scrolled PAST, never merely because it is
        out of view: boundingClientRect.top below zero means the buybox is above the viewport.
        Without that test the bar would also show while the customer is still above a buybox
        that sits lower down the page, offering to add before anything has been chosen.
      */
      onIntersection(entries) {
        entries.forEach((entry) => {
          const scrolledPast = entry.boundingClientRect.top < 0;
          this.classList.toggle('aurala-sticky-atc--visible', !entry.isIntersecting && scrolledPast);
        });
      }

      onVariantChange(event) {
        const payload = event && event.data;
        if (!payload || payload.sectionId !== this.dataset.mainSectionId) return;

        /*
          The price is read off the CTA in the section Dawn just re-rendered, which is the
          same source of truth assets/product-info.js uses for the button's own price, so the
          bar and the button can never disagree. Still Dawn's money formatting, rendered by
          Liquid from shop.money_format: there is no second money formatter here.

          It used to read #price-<sectionId>. That element only exists when a 'price' block is
          on the template, and it was removed from all three product templates during the
          rebuild, so getElementById returned null, the guard skipped, and the bar kept the
          first variant's price forever with nothing thrown. Exactly the bug already fixed
          once in product-info.js. .aurala-cta__price is inside the buy button itself, so it
          is present in every section response that has a buyable variant and cannot be
          deleted by a template edit without deleting the add to cart button with it.
        */
        const source = payload.html && payload.html.querySelector('.aurala-cta__price');
        if (source && this.priceTarget) this.priceTarget.textContent = source.textContent.trim();

        if (!this.submitButton) return;
        const available = !!(payload.variant && payload.variant.available);
        this.submitButton.disabled = !available;
        if (this.labelTarget) {
          const label = available
            ? this.submitButton.dataset.labelAvailable
            : this.submitButton.dataset.labelSoldOut;
          if (label) this.labelTarget.textContent = label;
        }
      }
    }
  );
}
